import { useMemo } from 'react';

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@growdash/design-system/atoms';
import { DataTable } from '@growdash/design-system/molecules';

// Define the structure for the user's invoice data
interface UserInvoice {
  date: string;
  amount: number;
  status: 'paid' | 'cancel' | 'refund'; // Add other statuses if applicable
}

export const InvoicesList = ({
  invoices,
  currency,
}: {
  invoices: Array<UserInvoice>;
  currency: string;
}) => {
  // Define columns for invoices table
  const invoiceColumns = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ row }: { row: { original: UserInvoice } }) => {
          const date = new Date(row.original.date);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        },
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }: { row: { original: UserInvoice } }) => {
          return `${row.original.amount} ${currency}`;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: UserInvoice } }) => {
          const status = row.original.status;
          let badgeVariant:
            | 'success'
            | 'destructive'
            | 'secondary'
            | 'outline' = 'outline';

          switch (status) {
            case 'paid':
              badgeVariant = 'success';
              break;
            case 'cancel':
              badgeVariant = 'destructive';
              break;
            case 'refund':
              badgeVariant = 'secondary';
              break;
            default:
              badgeVariant = 'outline';
          }

          return (
            <Badge variant={badgeVariant}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row justify-between items-center border-b border-muted-foreground/50 py-2 min-h-[53px]">
          <CardTitle>Invoices & Receipts</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <DataTable
            columns={invoiceColumns}
            data={invoices}
            isPaginationActive={true}
            isBorderActive={true}
          />
        </CardContent>
      </Card>
    </>
  );
};
